import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type Profile = Database['public']['Tables']['profiles']['Row'];
type Talent = Database['public']['Tables']['talents']['Row'];
type Brand = Database['public']['Tables']['brands']['Row'];
type Agent = Database['public']['Tables']['agents']['Row'];
type VentureCapital = Database['public']['Tables']['venture_capitals']['Row'];

export interface UserProfile extends Profile {
  talent?: Talent;
  brand?: Brand;
  agent?: Agent;
  venture_capital?: VentureCapital;
}

export const userService = {
  // Get all profiles with their associated data (public fields only for security)
  async getAllProfiles(): Promise<UserProfile[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        id,
        display_name,
        avatar_url,
        account_type,
        is_verified,
        created_at,
        talents(*),
        brands(*),
        agents(*),
        venture_capitals(*)
      `);

    if (error) {
      console.error('Error fetching profiles:', error);
      throw error;
    }

    return (data || []).map(profile => ({
      ...profile,
      // Security: Don't expose sensitive fields in public profile view
      email: '', // Hidden for security
      bio: '', // Hidden for security  
      location: '', // Hidden for security
      user_id: profile.id, // Use profile id as fallback
      updated_at: profile.created_at, // Use created_at as fallback
      setup_completed: false, // Default for public view
      beta_access: false, // Default for public view
      stripe_customer_id: null, // Hidden for security
      dismissed_tooltips: [], // Default for public view
      talent: profile.talents?.[0] || undefined,
      brand: profile.brands?.[0] || undefined,
      agent: profile.agents?.[0] || undefined,
      venture_capital: profile.venture_capitals?.[0] || undefined,
    }));
  },

  // Get profile by ID
  async getProfileById(id: string): Promise<UserProfile | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        *,
        talents(*),
        brands(*),
        agents(*),
        venture_capitals(*)
      `)
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching profile:', error);
      throw error;
    }

    if (!data) return null;

    return {
      ...data,
      talent: data.talents?.[0] || undefined,
      brand: data.brands?.[0] || undefined,
      agent: data.agents?.[0] || undefined,
      venture_capital: data.venture_capitals?.[0] || undefined,
    };
  },

  // Get profile by user_id (auth user id)
  async getProfileByUserId(userId: string): Promise<UserProfile | null> {
    console.log('userService.getProfileByUserId: Fetching profile for user:', userId);
    
    // Step 1: Fetch base profile only (safe via profiles RLS)
    const { data: base, error: baseErr } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (baseErr) {
      console.error('userService.getProfileByUserId: base profile error:', baseErr);
      return null;
    }

    if (!base) {
      console.log('userService.getProfileByUserId: no base profile found for', userId);
      return null;
    }

    const result: UserProfile = { ...base };

    // Step 2: Fetch role-specific data separately (best-effort, won't block profile load)
    try {
      if (base.account_type === 'talent') {
        const { data, error } = await supabase
          .from('talents')
          .select('*')
          .eq('profile_id', base.id)
          .maybeSingle();
        if (error) console.warn('userService.getProfileByUserId: talents fetch warn:', error);
        result.talent = data || undefined;
      } else if (base.account_type === 'brand') {
        const { data, error } = await supabase
          .from('brands')
          .select('*')
          .eq('profile_id', base.id)
          .maybeSingle();
        if (error) console.warn('userService.getProfileByUserId: brands fetch warn:', error);
        result.brand = data || undefined;
      } else if (base.account_type === 'agent') {
        const { data, error } = await supabase
          .from('agents')
          .select('*')
          .eq('profile_id', base.id)
          .maybeSingle();
        if (error) console.warn('userService.getProfileByUserId: agents fetch warn:', error);
        result.agent = data || undefined;
      } else if (base.account_type === 'venture') {
        const { data, error } = await supabase
          .from('venture_capitals')
          .select('*')
          .eq('profile_id', base.id)
          .maybeSingle();
        if (error) console.warn('userService.getProfileByUserId: venture fetch warn:', error);
        result.venture_capital = data || undefined;
      }
    } catch (e) {
      console.warn('userService.getProfileByUserId: role-specific fetch error, continuing:', e);
    }

    console.log('userService.getProfileByUserId: final result:', result);
    return result;
  },

  // Get talents only - convert to legacy format
  async getTalents(): Promise<any[]> {
    const { data, error } = await supabase
      .from('talents')
      .select(`
        *,
        profiles(id, display_name, avatar_url, account_type, is_verified, created_at)
      `);

    if (error) {
      console.error('Error fetching talents:', error);
      throw error;
    }

    // Convert to legacy talent format
    return (data || []).map(talent => ({
      id: talent.profile_id,
      name: talent.name,
      sport: talent.sport,
      talentType: talent.talent_type,
      photo: talent.photo,
      avatarUrl: talent.profiles?.avatar_url,
      totalFollowers: talent.total_followers,
      totalRevenue: Number(talent.total_revenue),
      totalHoldings: talent.total_holdings,
      avgBusinessImpact: Number(talent.avg_business_impact),
      joinDate: talent.join_date || talent.created_at?.split('T')[0],
      status: talent.status,
      tier: talent.tier,
      achievements: talent.achievements,
      stats: talent.stats,
      socialProfiles: talent.social_profiles,
      agentContact: talent.agent_contact,
      // Security: Don't expose sensitive profile fields for other users
      bio: '', // Hidden for security
      location: '', // Hidden for security
      isVerified: talent.profiles?.is_verified
    }));
  },

  // Update talent data
  async updateTalent(id: string, updates: Partial<Talent>): Promise<Talent> {
    const { data, error } = await supabase
      .from('talents')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating talent:', error);
      throw error;
    }

    return data;
  },

  // Update profile data
  async updateProfile(id: string, updates: Partial<Profile>): Promise<Profile> {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating profile:', error);
      throw error;
    }

    return data;
  },

  // Populate sample data
  async populateSampleData(): Promise<void> {
    const { data, error } = await supabase.functions.invoke('populate-sample-data');

    if (error) {
      console.error('Error populating sample data:', error);
      throw error;
    }

    return data;
  }
};